import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import 'flag-icons/css/flag-icons.min.css';

// Security function to sanitize user input and prevent XSS
const sanitizeText = (text) => {
  if (!text) return '';
  
  const cleanText = String(text)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[BLOCKED SCRIPT]')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
    
  return cleanText;
};

const SecureText = ({ children, className = '', title = '' }) => {
  const sanitizedText = sanitizeText(children);
  const sanitizedTitle = sanitizeText(title);
  
  return (
    <span 
      className={className} 
      title={sanitizedTitle}
      dangerouslySetInnerHTML={undefined}
    >
      {sanitizedText}
    </span>
  );
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, getValidJWTToken } = useAuth();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('uploads');
  const [userUploads, setUserUploads] = useState([]);
  const [userChallenges, setUserChallenges] = useState([]);
  const [userVotes, setUserVotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [deletingUploadId, setDeletingUploadId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Settings state
  const [editUsername, setEditUsername] = useState('');
  const [editDiscordName, setEditDiscordName] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageBlur, setSelectedImageBlur] = useState(null);
  const [revealBlurredImage, setRevealBlurredImage] = useState(false);
  
  // Language dropdown state
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef(null);
  
  // Available languages
  const languages = [
    { code: 'fr', name: 'Français', flag: 'fi fi-fr' },
    { code: 'en', name: 'English', flag: 'fi fi-us' }
  ];

  const fetchUserUploads = useCallback(async () => {
    try {
      console.log('Fetching uploads for email:', user.email);
      const token = await getValidJWTToken();
      if (!token) {
        setUserUploads([]);
        return;
      }

      const response = await fetch('/.netlify/functions/user?action=uploads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const result = await response.json();
      console.log('Full response from server:', result);
      
      if (result.success) {
        setUserUploads(result.data || []);
        console.log('Successfully fetched user uploads:', result.data);
        console.log('Debug info:', result.debug);
      } else {
        console.error('Failed to fetch user uploads:', result.message);
        console.error('Server error:', result.error);
        setUserUploads([]);
      }
    } catch (error) {
      console.error('Network error fetching user uploads:', error);
      setUserUploads([]);
    }
  }, [user.email, getValidJWTToken]);

  const fetchUserChallenges = useCallback(async () => {
    try {
      if (!user?.email) {
        setUserChallenges([]);
        return;
      }

      const token = await getValidJWTToken();
      if (!token) {
        setUserChallenges([]);
        return;
      }

      const response = await fetch('/.netlify/functions/user?action=challenges', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      console.log('Challenges response status:', response.status);

      const result = await response.json();
      console.log('Full challenges response from server:', result);
      
      if (result.success) {
        setUserChallenges(result.data || []);
        console.log('Successfully fetched user challenges:', result.data);
        console.log('Challenges debug info:', result.debug);
      } else {
        console.error('Failed to fetch user challenges:', result.message);
        console.error('Challenges server error:', result.error);
        setUserChallenges([]);
      }
    } catch (error) {
      console.error('Network error fetching user challenges:', error);
      setUserChallenges([]);
    }
  }, [user?.email, getValidJWTToken]);

  const fetchUserVotes = useCallback(async () => {
    try {
      console.log('Fetching votes for email:', user.email);
      
      if (!user?.email) {
        console.error('No user email available for fetching votes');
        setUserVotes([]);
        return;
      }

      const token = await getValidJWTToken();
      if (!token) {
        setUserVotes([]);
        return;
      }
      
      const response = await fetch('/.netlify/functions/user?action=user-votes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email
        })
      });

      console.log('Votes response status:', response.status);

      const result = await response.json();
      console.log('Full votes response from server:', result);
      
      if (result.success) {
        setUserVotes(result.data || []);
        console.log('Successfully fetched user votes:', result.data);
        console.log('Votes debug info:', result.debug);
      } else {
        console.error('Failed to fetch user votes:', result.message);
        console.error('Votes server error:', result.error);
        setUserVotes([]);
      }
    } catch (error) {
      console.error('Network error fetching user votes:', error);
      setUserVotes([]);
    }
  }, [user.email, getValidJWTToken]);

  const fetchUserData = useCallback(async () => {
    if (!user?.email) return;
    
    setLoading(true);
    setDataLoaded(false);
    try {
      // Fetch user uploads from the database
      await fetchUserUploads();
      
      // Fetch user challenges from the database
      await fetchUserChallenges();
      
      // Fetch user votes from the database
      await fetchUserVotes();
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  }, [user?.email, fetchUserUploads, fetchUserChallenges, fetchUserVotes]);

  useEffect(() => {
    // No need to check authentication here as UserProtectedRoute handles it
    fetchUserData();
  }, [fetchUserData]);
  
  // Restore active tab from localStorage after reload
  useEffect(() => {
    const savedTab = localStorage.getItem('dashboardActiveTab');
    if (savedTab) {
      setActiveTab(savedTab);
      localStorage.removeItem('dashboardActiveTab');
    }
  }, []);
  
  // Initialize settings when user data changes
  useEffect(() => {
    if (user) {
      setEditUsername(user.username || '');
      setEditDiscordName(user.discord_name || '');
    }
  }, [user]);
  
  // Close language dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Redirect non-admin users away from challenges tab
  useEffect(() => {
    if (activeTab === 'challenges' && user?.role?.toLowerCase() !== 'admin') {
      setActiveTab('uploads');
    }
  }, [activeTab, user?.role]);

  const deleteUpload = async (uploadId) => {
    if (!user?.email) {
      console.error('No user email available for deletion');
      return;
    }

    setDeletingUploadId(uploadId);
    
    try {
      console.log('Deleting upload:', uploadId);
      
      const response = await fetch('/.netlify/functions/delete-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uploadId: uploadId,
          userEmail: user.email
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('successfully deleted.');
        // Remove the deleted upload from the local state
        setUserUploads(prevUploads => prevUploads.filter(upload => upload.id !== uploadId));
        setShowDeleteConfirm(null);
        showToast(t('successfullyDeleted'));
      } else {
        console.error('Failed to delete upload:', result.message);
        showToast(t('failedToDelete') + ': ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      showToast(t('errorDeletingTryAgain'), 'error');
    } finally {
      setDeletingUploadId(null);
    }
  };

  const handleDeleteClick = (uploadId, uploadTitle) => {
    setShowDeleteConfirm({ id: uploadId, title: uploadTitle });
  };

  const confirmDelete = () => {
    if (showDeleteConfirm) {
      deleteUpload(showDeleteConfirm.id);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };
  
  // Save settings function
  const saveSettings = async () => {
    if (!user?.email) {
      showToast('No user email available', 'error');
      return;
    }
    
    // Validate username
    if (editUsername && editUsername.trim().length < 3) {
      showToast('Username must be at least 3 characters', 'error');
      return;
    }
    
    if (editUsername && editUsername.trim().length > 50) {
      showToast('Username must be less than 50 characters', 'error');
      return;
    }
    
    setSavingSettings(true);
    
    try {
      const updateData = {
        email: user.email
      };
      
      if (editUsername !== user.username) {
        updateData.username = editUsername.trim();
      }
      
      if (editDiscordName !== user.discord_name) {
        updateData.discord_name = editDiscordName.trim();
      }
      
      const token = await getValidJWTToken();
      if (!token) {
        showToast('Session expired. Please sign in again.', 'error');
        return;
      }

      const response = await fetch('/.netlify/functions/update-user-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        showToast('Settings saved successfully!', 'success');
        // Save current tab before reload
        localStorage.setItem('dashboardActiveTab', 'settings');
        // Reload to get fresh data
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        showToast(result.message || 'Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Error saving settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };
  
  // Language selection handler
  const handleLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
    setShowLanguageDropdown(false);
    console.log('Language selected:', languageCode);
  };

  // Get current language data
  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[1]; // Default to English (index 1)
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    // Handle logout logic
    logout();
  };

  const getStatusInfo = (status) => {
    switch (parseInt(status)) {
      case 0: return { text: t('pending'), color: 'bg-yellow-600' };
      case 1: return { text: t('approved'), color: 'bg-green-600' };
      case 2: return { text: t('rejected'), color: 'bg-red-600' };
      case 3: return { text: t('deleted'), color: 'bg-gray-600' };
      case 9: return { text: t('review'), color: 'bg-blue-600' };
      default: return { text: t('unknown'), color: 'bg-gray-600' };
    }
  };

  const getChallengeStatusInfo = (status) => {
    switch (status) {
      case 'active': return { text: t('active'), color: 'bg-green-600' };
      case 'ended': return { text: t('ended'), color: 'bg-gray-600' };
      default: return { text: t('unknown'), color: 'bg-gray-600' };
    }
  };

  // Show loading screen while user data is loading
  if ((isAuthenticated && !user) || (isAuthenticated && user && !dataLoaded)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  // Only redirect after we're sure about the auth state
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex overflow-hidden">
      {/* Sidebar - Responsive */}
      <div className={`
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static fixed inset-y-0 left-0 z-50
        w-64 bg-gray-800 flex flex-col h-full
        transition-transform duration-300 ease-in-out
      `}>
        {/* Mobile Close Button */}
        <div className="lg:hidden flex justify-end p-4">
          <button
            onClick={closeMobileMenu}
            className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Logo */}
        <div className="p-6 border-b border-gray-700">
          <h1 
            className="text-2xl font-bold text-pink-500 cursor-pointer hover:text-pink-400 transition-colors duration-200"
            onClick={() => navigate('/')}
            title="Retour à l'accueil"
          >
            PIMP MY DROP
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-6 space-y-6 overflow-y-auto">
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('myProfile')}</h3>
            <div className="space-y-2">
              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeTab === 'uploads' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => setActiveTab('uploads')}
              >
                <i className="fas fa-upload text-blue-500 w-5"></i>
                <span className="font-medium">{t('myUploads')}</span>
              </div>
              {/* Only show Mes challenges for admin users */}
              {user?.role?.toLowerCase() === 'admin' && (
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    activeTab === 'challenges' ? 'bg-gray-700' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => setActiveTab('challenges')}
                >
                  <i className="fas fa-trophy text-yellow-500 w-5"></i>
                  <span>{t('myChallenges')}</span>
                </div>
              )}
              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeTab === 'votes' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => setActiveTab('votes')}
              >
                <i className="fas fa-vote-yea text-green-500 w-5"></i>
                <span>{t('myVotes')}</span>
              </div>
              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeTab === 'settings' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => setActiveTab('settings')}
              >
                <i className="fas fa-cog text-purple-500 w-5"></i>
                <span>{t('settings') || 'Settings'}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('navigation')}</h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-3 p-3 hover:bg-gray-700 rounded-lg cursor-pointer transition-colors w-full text-left"
              >
                <i className="fas fa-home text-cyan-500 w-5"></i>
                <span>{t('backToHome')}</span>
              </button>
            </div>
          </div>
        </nav>

        {/* User Info */}
        <div className="p-6 border-t border-gray-700 bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-gray-400">{t('connectedAs')}</p>
              <div className="flex items-center space-x-3">
              
                
                {/* Language Dropdown */}
                <div className="relative" ref={languageDropdownRef}>
                  <button
                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                    className="flex items-center space-x-1 hover:text-yellow-400 text-gray-300 transition-colors duration-300"
                  >
                    <span 
                      className={`${getCurrentLanguage().flag}`}
                      style={{
                        fontSize: '14px',
                        width: '18px',
                        height: '13px',
                        borderRadius: '2px',
                        display: 'inline-block'
                      }}
                      role="img" 
                      aria-label={`${getCurrentLanguage().name} flag`}
                    />
                    <svg 
                      className={`h-3 w-3 transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Language Dropdown Menu */}
                  {showLanguageDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 w-36 bg-[#1A1A1A] rounded-lg shadow-xl z-50 border border-gray-600">
                      <div className="py-2">
                        {languages.map((language) => (
                          <button
                            key={language.code}
                            onClick={() => handleLanguageSelect(language.code)}
                            className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 transition-colors ${
                              currentLanguage === language.code ? 'bg-gray-700 text-yellow-400' : 'text-white'
                            }`}
                          >
                            <span 
                              className={language.flag}
                              style={{
                                fontSize: '14px',
                                width: '18px',
                                height: '13px',
                                borderRadius: '2px',
                                display: 'inline-block'
                              }}
                              role="img" 
                              aria-label={`${language.name} flag`}
                            />
                            <span className="text-sm font-medium">{language.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <p className="font-medium">{user?.name || 'User'}</p>
              </div>
              <p className="text-xs text-gray-500">{user?.email || 'user@example.com'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 p-2 hover:bg-gray-700 rounded-lg transition-colors ml-2"
              title={t('logout')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-gray-800 p-[1.20rem] flex justify-between items-center flex-shrink-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div>
              <h2 className="text-xl lg:text-2xl font-bold">{t('myDashboard')}</h2>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 lg:space-x-4">
            <button 
              onClick={() => navigate('/submit')}
              className="bg-pink-600 hover:bg-pink-700 px-3 lg:px-4 py-2 rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 transition-colors text-sm lg:text-base"
            >
              <span>+</span>
              <span className="hidden sm:inline">{t('newDesign')}</span>
              <span className="sm:hidden">Design</span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-3 lg:p-6">
            {/* Stats Cards */}
            <div className={`grid gap-3 lg:gap-6 mb-6 lg:mb-8 ${user?.role?.toLowerCase() === 'admin' ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>
              <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-4 lg:p-6 rounded-xl text-white">
                <div className="flex items-center justify-between mb-2 lg:mb-4">
                  <div className="bg-white bg-opacity-20 p-1.5 lg:p-2 rounded-lg">
                    <svg className="w-4 h-4 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-xs lg:text-sm font-medium opacity-90">{t('myDesigns')}</h3>
                <p className="text-lg lg:text-3xl font-bold">{userUploads.filter(upload => parseInt(upload.status) !== 3).length}</p>
                <p className="text-xs lg:text-sm opacity-75 mt-1 lg:mt-2">{t('designsUploaded')}</p>
              </div>

              {/* Only show Mes Challenges card for admin users */}
              {user?.role?.toLowerCase() === 'admin' && (
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-4 lg:p-6 rounded-xl text-white">
                  <div className="flex items-center justify-between mb-2 lg:mb-4">
                    <div className="bg-white bg-opacity-20 p-1.5 lg:p-2 rounded-lg">
                      <svg className="w-4 h-4 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-xs lg:text-sm font-medium opacity-90">{t('myChallenges')}</h3>
                  <p className="text-lg lg:text-3xl font-bold">{userChallenges.length}</p>
                  <p className="text-xs lg:text-sm opacity-75 mt-1 lg:mt-2">{t('challengesCreated')}</p>
                </div>
              )}

              <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 lg:p-6 rounded-xl text-white">
                <div className="flex items-center justify-between mb-2 lg:mb-4">
                  <div className="bg-white bg-opacity-20 p-1.5 lg:p-2 rounded-lg">
                    <svg className="w-4 h-4 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-xs lg:text-sm font-medium opacity-90">{t('myVotes')}</h3>
                <p className="text-lg lg:text-3xl font-bold">{userVotes.length}</p>
                <p className="text-xs lg:text-sm opacity-75 mt-1 lg:mt-2">{t('designsVoted')}</p>
              </div>

              <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-4 lg:p-6 rounded-xl text-white">
                <div className="flex items-center justify-between mb-2 lg:mb-4">
                  <div className="bg-white bg-opacity-20 p-1.5 lg:p-2 rounded-lg">
                    <svg className="w-4 h-4 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-xs lg:text-sm font-medium opacity-90">{t('myVotes')}</h3>
                <p className="text-lg lg:text-3xl font-bold">
                  {userUploads.filter(upload => parseInt(upload.status) !== 3).length > 0 ? 
                    Math.round(userUploads.filter(upload => parseInt(upload.status) !== 3).reduce((acc, upload) => acc + (upload.vote || 0), 0) / userUploads.filter(upload => parseInt(upload.status) !== 3).length)
                    : '0'
                  }
                </p>
                <p className="text-xs lg:text-sm opacity-75 mt-1 lg:mt-2">{t('averageVotes')}</p>
              </div>
            </div>

            {/* Tabbed Interface */}
            <div className="bg-gray-800 rounded-xl">
              {/* Tab Headers */}
              <div className="flex border-b border-gray-700 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('uploads')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'uploads'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('myUploads')}
                </button>
                {/* Only show Mes Challenges tab for admin users */}
                {user?.role?.toLowerCase() === 'admin' && (
                  <button
                    onClick={() => setActiveTab('challenges')}
                    className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                      activeTab === 'challenges'
                        ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    {t('myChallenges')}
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('votes')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'votes'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('myVotes')}
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'settings'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('settings') || 'Settings'}
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-3 lg:p-6">
                {/* Uploads Tab */}
                {activeTab === 'uploads' && (
                  <div>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto"></div>
                      </div>
                    ) : userUploads.length === 0 ? (
                      <div className="text-center py-8">
                        <button 
                          onClick={() => navigate('/submit')}
                          className="text-blue-500 hover:text-blue-400 underline text-lg font-medium transition-colors"
                        >
                          {t('noUploadYet')}
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                        {userUploads.filter(upload => parseInt(upload.status) !== 3).map((upload) => {
                          const statusInfo = getStatusInfo(upload.status);
                          return (
                            <div key={upload.id} className="bg-gray-700 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                              <div className="aspect-square bg-gray-600 relative">
                                {upload.image_url ? (
                                  <img 
                                    src={upload.image_url} 
                                    alt={upload.title}
                                    className={`w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity ${upload.blur ? 'blur-xl' : ''}`}
                                    onClick={() => {
                                      setSelectedImage(upload.image_url);
                                      setSelectedImageBlur(upload.blur || null);
                                      setRevealBlurredImage(false);
                                      setShowImageModal(true);
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <i className="fas fa-image text-2xl"></i>
                                  </div>
                                )}
                                <div className="absolute top-3 right-3 flex items-center space-x-2">
                                  <span className={`${statusInfo.color} text-white px-2 py-1 rounded text-xs font-medium`}>
                                    {statusInfo.text}
                                  </span>
                                  <button
                                    onClick={() => handleDeleteClick(upload.id, upload.title)}
                                    disabled={deletingUploadId === upload.id}
                                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white p-1 rounded transition-colors duration-200"
                                    title={t('deleteDesign')}
                                  >
                                    {deletingUploadId === upload.id ? (
                                      <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    ) : (
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              </div>
                              
                              <div className="p-3 lg:p-4 space-y-2">
                                <SecureText className="font-semibold text-white text-sm lg:text-base leading-tight block">
                                  {upload.title}
                                </SecureText>
                                
                                {(upload.challenge_id !== 0 && upload.challenge_name) && (
                                  <div className="bg-gray-600 rounded p-2 text-xs">
                                    <span className="text-cyan-400">Challenge: </span>
                                    <SecureText className="text-gray-200">
                                      {upload.challenge_name}
                                    </SecureText>
                                  </div>
                                )}
                                
                                <div className="flex justify-between items-center text-xs text-gray-400">
                                  <span>{upload.vote || 0} {t('votes')}</span>
                                  <span>{t('status')} {getStatusInfo(upload.status).text}</span>
                                </div>
                                
                                <div className="text-xs text-gray-500">
                                  {new Date(upload.submitted_at).toLocaleDateString('fr-FR')}
                                </div>
                                
                                {/* Show rejection reason if status is rejected */}
                                {parseInt(upload.status) === 2 && upload.reason && (
                                  <div className="bg-red-900 bg-opacity-30 border border-red-600 rounded p-2 mt-2">
                                    <div className="text-xs text-red-400 font-medium mb-1">{t('reasonForRejection')}</div>
                                    <SecureText className="text-xs text-red-300">
                                      {upload.reason}
                                    </SecureText>
                                  </div>
                                )}
                                
                                {/* Show blur reason if image is blurred */}
                                {upload.blur && (
                                  <div className="bg-red-900/30 border border-red-700 rounded p-2 mt-2">
                                    <p className="text-red-400 text-xs leading-relaxed">
                                      <i className="fas fa-eye-slash mr-1"></i>
                                      {upload.blur}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Challenges Tab */}
                {activeTab === 'challenges' && user?.role?.toLowerCase() === 'admin' && (
                  <div>
                    {userChallenges.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-2">{t('noChallenge')}</div>
                        <button 
                          onClick={() => document.querySelector('[data-challenge-button]')?.click()}
                          className="text-pink-500 hover:text-pink-400 underline"
                        >
                          {t('createFirstChallenge')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {userChallenges.map((challenge) => (
                          <div key={challenge.id} className="bg-gray-700 rounded-xl p-4 lg:p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1 mr-4">
                                <SecureText className="text-lg font-semibold text-white">
                                  {challenge.title}
                                </SecureText>
                                <SecureText className="text-sm text-gray-300 mt-1 block">
                                  {challenge.description}
                                </SecureText>
                                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-400">
                                  <div>
                                    <span>{t('createdOn')} </span>
                                    <span className="text-gray-300">
                                      {new Date(challenge.created_at).toLocaleDateString('fr-FR')}
                                    </span>
                                  </div>
                                  <div>
                                    <span>{t('expiresOn')} </span>
                                    <span className="text-gray-300">
                                      {new Date(challenge.expiration).toLocaleDateString('fr-FR')} à {new Date(challenge.expiration).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-col items-end space-y-2">
                                <span className={`${getChallengeStatusInfo(challenge.status).color} text-white px-3 py-1 rounded text-sm font-medium`}>
                                  {getChallengeStatusInfo(challenge.status).text}
                                </span>
                                {challenge.color && (
                                  <div 
                                    className="w-6 h-6 rounded border border-gray-500"
                                    style={{ backgroundColor: challenge.color }}
                                    title={`Couleur du challenge: ${challenge.color}`}
                                  />
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-400">{t('participants')} </span>
                                <span className="text-white font-medium">{challenge.participants || 0}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">{t('submissions')} </span>
                                <span className="text-white font-medium">{challenge.submissions || 0}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Votes Tab */}
                {activeTab === 'votes' && (
                  <div>
                    {userVotes.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-2">{t('noVoteYet')}</div>
                        <button 
                          onClick={() => navigate('/gallery')}
                          className="text-pink-500 hover:text-pink-400 underline"
                        >
                          {t('discoverDesigns')}
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                        {userVotes.map((vote) => (
                          <div key={vote.id} className="bg-gray-700 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                            {/* Design Image */}
                            <div className="aspect-square bg-gray-600 relative">
                              {vote.design_image ? (
                                <img 
                                  src={vote.design_image} 
                                  alt={vote.design_title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <i className="fas fa-image text-2xl"></i>
                                </div>
                              )}
                              <div className="absolute top-3 right-3">
                                <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">
                                  {t('voted')}
                                </span>
                              </div>
                            </div>
                            
                            {/* Design Info */}
                            <div className="p-4 space-y-2">
                              <SecureText className="font-semibold text-white text-base leading-tight block">
                                {vote.design_title}
                              </SecureText>
                              
                              <div className="text-sm text-gray-400">
                                <span>{t('by')} </span>
                                <SecureText className="text-cyan-400">
                                  {vote.design_author}
                                </SecureText>
                              </div>
                              
                              {vote.challenge_name && (
                                <div className="bg-gray-600 rounded p-2 text-xs">
                                  <span className="text-yellow-400">{t('challenge')} </span>
                                  <SecureText className="text-gray-200">
                                    {vote.challenge_name}
                                  </SecureText>
                                </div>
                              )}
                              
                              <div className="flex justify-between items-center text-xs text-gray-400 pt-2">
                                <span>{vote.vote_count || 0} {t('votesTotal')}</span>
                                <span>
                                  {new Date(vote.voted_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="max-w-2xl mx-auto">
                    <div className="bg-gray-700 rounded-xl p-6 space-y-6">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <i className="fas fa-user-edit text-purple-500 mr-3"></i>
                          {t('profileSettings') || 'Profile Settings'}
                        </h3>
                      </div>

                      {/* Username Field */}
                      <div>
                        <label htmlFor="username" className="block text-gray-300 font-medium mb-2">
                          Username *
                        </label>
                        <input
                          type="text"
                          id="username"
                          value={editUsername}
                          onChange={(e) => setEditUsername(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          placeholder="Enter your username"
                          maxLength={50}
                        />
                      </div>

                      {/* Discord Name Field */}
                      <div>
                        <label htmlFor="discordName" className="block text-gray-300 font-medium mb-2">
                          Discord
                        </label>
                        <input
                          type="text"
                          id="discordName"
                          value={editDiscordName}
                          onChange={(e) => setEditDiscordName(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          placeholder="YourName#1234"
                          maxLength={100}
                        />
                      </div>

                      {/* Save Button */}
                      <div className="pt-4">
                        <button
                          onClick={saveSettings}
                          disabled={savingSettings || !editUsername.trim() || editUsername.trim().length < 3}
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition duration-300 flex items-center justify-center space-x-2"
                        >
                          {savingSettings ? (
                            <>
                              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>{t('saving') || 'Saving...'}</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save"></i>
                              <span>{t('saveChanges') || 'Save Changes'}</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* Current User Info */}
                      <div className="pt-4 border-t border-gray-600">
                        <h4 className="text-sm font-medium text-gray-400 mb-3">
                          User info
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">{t('email') || 'Email'}:</span>
                            <span className="text-white">{user?.email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">{t('currentUsername') || 'Current Username'}:</span>
                            <span className="text-white">{user?.username || 'Not set'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">{t('currentDiscordName') || 'Current Discord'}:</span>
                            <span className="text-white">{user?.discord_name || 'Not set'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm w-full mx-4 transform transition-all duration-300 ease-in-out ${
          toast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}>
          <div className={`p-4 rounded-lg shadow-lg text-white flex items-center space-x-3 ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
          }`}>
            <div className="flex-shrink-0">
              {toast.type === 'error' ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast(null)}
              className="flex-shrink-0 text-white hover:text-gray-200"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {t('deleteDesign')}
              </h3>
              <p className="text-sm text-gray-300 mb-6">
                {t('areYouSure')} "<SecureText>{showDeleteConfirm.title}</SecureText>" ? {t('thisActionIrreversible')}
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={cancelDelete}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded transition-colors duration-200"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deletingUploadId !== null}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white py-2 px-4 rounded transition-colors duration-200 flex items-center justify-center"
                >
                  {deletingUploadId !== null ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('deleting')}
                    </>
                  ) : (
                    t('delete')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Popup Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={() => {
            setShowImageModal(false);
            setSelectedImage(null);
            setSelectedImageBlur(null);
            setRevealBlurredImage(false);
          }}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowImageModal(false);
                setSelectedImage(null);
                setSelectedImageBlur(null);
                setRevealBlurredImage(false);
              }}
              className="absolute top-4 right-4 z-10 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image Container */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              {/* Image */}
              <img 
                src={selectedImage} 
                alt="Enlarged view" 
                className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl ${selectedImageBlur && !revealBlurredImage ? 'blur-xl' : ''}`}
              />
              
              {/* View Image Button - Only show for blurred images that haven't been revealed */}
              {selectedImageBlur && !revealBlurredImage && (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <button
                    onClick={() => setRevealBlurredImage(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition-all transform hover:scale-105 flex items-center space-x-3"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>{t('viewImage')}</span>
                  </button>
                  <p className="mt-4 text-red-400 text-sm max-w-md text-center px-4">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {selectedImageBlur}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard; 